import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, ImageOverlay, Marker, Tooltip as LeafletTooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { 
  Box, 
  Typography, 
  Fab, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField, 
  Button, 
  MenuItem, 
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Slider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  Add as AddIcon, 
  Close as CloseIcon, 
  Delete as DeleteIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { 
  getMapById, 
  getPinsByMapId, 
  addPin, 
  updatePin, 
  deletePin, 
  PIN_CATEGORIES 
} from '../../services/mapService';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';
import './LeafletMap.css';

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom D&D themed icons for different pin categories
const createCustomIcon = (category, size = 1) => {
  const categoryConfig = PIN_CATEGORIES.find(cat => cat.value === category) || PIN_CATEGORIES[0];
  const emoji = categoryConfig.label.split(' ')[0];
  const iconSize = Math.round(30 * size);
  const fontSize = Math.round(14 * size);
  const borderWidth = Math.max(2, Math.round(3 * size));
  
  return L.divIcon({
    html: `
      <div class="pin-icon" style="
        background-color: ${categoryConfig.color}; 
        width: ${iconSize}px; 
        height: ${iconSize}px; 
        border-radius: 50%; 
        border: ${borderWidth}px solid white; 
        box-shadow: 0 3px 10px rgba(0,0,0,0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${fontSize}px;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
      " 
      onmouseover="
        this.style.transform='scale(1.15)'; 
        this.style.boxShadow='0 6px 20px rgba(217, 141, 42, 0.4), 0 2px 8px rgba(0,0,0,0.8)'; 
        this.style.zIndex='1001';
        this.style.filter='brightness(1.1)';
      "
      onmouseout="
        this.style.transform='scale(1)'; 
        this.style.boxShadow='0 3px 10px rgba(0,0,0,0.6)'; 
        this.style.zIndex='auto';
        this.style.filter='brightness(1)';
      "
      >${emoji}</div>
    `,
    className: 'custom-div-icon pin-marker',
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize / 2]
  });
};

// Component to handle map clicks for adding pins
const MapClickHandler = ({ isAddingPin, onAddPin, isUltraAuth }) => {
  useMapEvents({
    click: (e) => {
      if (isAddingPin && isUltraAuth) {
        const { lat, lng } = e.latlng;
        onAddPin(lat, lng);
      }
    },
  });
  return null;
};



const LeafletMapViewer = () => {
  const { mapId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { firestoreUser, isUltraAuth } = useAuth();
  const mapRef = useRef(null);

  const [map, setMap] = useState(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [pinDialog, setPinDialog] = useState({ open: false, lat: 0, lng: 0, pin: null });
  
  // Enhanced features
  const [visibleCategories, setVisibleCategories] = useState(new Set(PIN_CATEGORIES.map(cat => cat.value)));
  const [pinSize, setPinSize] = useState(1);
  
  // Pin to focus from search
  const [highlightedPinId, setHighlightedPinId] = useState(null);

  useEffect(() => {
    loadMapData();
  }, [mapId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle pin focus from search
  useEffect(() => {
    const pinId = searchParams.get('pinId');
    if (pinId && pins.length > 0 && mapRef.current) {
      const targetPin = pins.find(p => p.id === pinId);
      if (targetPin) {
        // Set highlighted pin
        setHighlightedPinId(pinId);
        
        // Center map on the pin
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.setView([targetPin.position.lat, targetPin.position.lng], 3, {
              animate: true,
              duration: 1
            });
          }
          
          // Open pin dialog
          setTimeout(() => {
            handlePinClick(targetPin);
          }, 500);
          
          // Clear highlight after 3 seconds
          setTimeout(() => {
            setHighlightedPinId(null);
          }, 3000);
        }, 300);
      }
    }
  }, [searchParams, pins]);

  const loadMapData = async () => {
    try {
      setLoading(true);
      const [mapData, pinsData] = await Promise.all([
        getMapById(mapId),
        getPinsByMapId(mapId)
      ]);
      setMap(mapData);
      setPins(pinsData);
    } catch (error) {
      console.error('Error loading map:', error);
      setError('Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPin = (lat, lng) => {
    setPinDialog({ open: true, lat, lng, pin: null });
    setIsAddingPin(false);
  };

  const handlePinClick = (pin) => {
    setPinDialog({ open: true, lat: 0, lng: 0, pin });
  };

  const handlePinSave = async (pinData) => {
    try {
      if (pinDialog.pin) {
        // Update existing pin
        await updatePin(pinDialog.pin.id, pinData);
      } else {
        // Add new pin
        const newPinData = {
          lat: pinDialog.lat,
          lng: pinDialog.lng,
          ...pinData,
          createdBy: firestoreUser.uid,
          createdByName: firestoreUser.displayName || 'Unknown User'
        };
        await addPin(mapId, newPinData);
      }
      await loadMapData(); // Reload pins
      setPinDialog({ open: false, lat: 0, lng: 0, pin: null });
    } catch (error) {
      console.error('Error saving pin:', error);
      setError('Failed to save pin');
    }
  };

  const handlePinDelete = async () => {
    if (!pinDialog.pin) return;
    
    try {
      await deletePin(pinDialog.pin.id);
      await loadMapData(); // Reload pins
      setPinDialog({ open: false, lat: 0, lng: 0, pin: null });
    } catch (error) {
      console.error('Error deleting pin:', error);
      setError('Failed to delete pin');
    }
  };

  const handleCategoryToggle = (category) => {
    const newVisible = new Set(visibleCategories);
    if (newVisible.has(category)) {
      newVisible.delete(category);
    } else {
      newVisible.add(category);
    }
    setVisibleCategories(newVisible);
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
        <Typography sx={{ ml: 2, color: '#F5F5F5' }}>Loading map...</Typography>
      </Box>
    );
  }

  if (error && !map) {
    return (
      <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#2c2c2c' }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/maps')} sx={{ mt: 2 }}>
          Back to Maps
        </Button>
      </Box>
    );
  }

  // Calculate map bounds based on image dimensions
  const imageBounds = [[0, 0], [100, 100]]; // Normalized coordinates

  return (
    <Box sx={{ 
      height: '100vh', 
      backgroundColor: '#2c2c2c', 
      position: 'relative'
    }}>
      {/* Header */}
      <Paper sx={{ 
        position: 'absolute', 
        top: 16, 
        left: 16, 
        right: 16, 
        zIndex: 1000,
        backgroundColor: '#3c3c3c',
        border: '1px solid #555',
        p: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/maps')}
              sx={{ color: '#d98d2a' }}
            >
              <BackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ 
              color: '#d98d2a', 
              fontFamily: '"Georgia", serif',
              fontWeight: 'bold'
            }}>
              {map?.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${pins.filter(pin => visibleCategories.has(pin.category)).length} pins`} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(217, 141, 42, 0.2)',
                color: '#d98d2a'
              }}
            />
          </Box>
        </Box>
        {map?.description && (
          <Typography variant="body2" sx={{ color: '#A9A9A9', mt: 1 }}>
            {map.description}
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Category Filter Panel */}
      <Paper sx={{ 
        position: 'absolute', 
        top: 140, 
        left: 220, 
        zIndex: 1000,
        backgroundColor: '#3c3c3c',
        border: '1px solid #555',
        p: 2,
        maxHeight: '60vh',
        overflowY: 'auto',
        minWidth: 200
      }}>
        <Typography variant="subtitle2" sx={{ color: '#d98d2a', mb: 1, fontWeight: 'bold' }}>
          Pin Categories
        </Typography>
        {PIN_CATEGORIES.map((category) => (
          <Box key={category.value} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={visibleCategories.has(category.value)}
                  onChange={() => handleCategoryToggle(category.value)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: category.color,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: category.color,
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: category.color
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#F5F5F5', fontSize: '0.8rem' }}>
                    {category.label}
                  </Typography>
                </Box>
              }
              sx={{ margin: 0 }}
            />
          </Box>
        ))}
        
        <Typography variant="subtitle2" sx={{ color: '#d98d2a', mt: 2, mb: 1, fontWeight: 'bold' }}>
          Pin Size
        </Typography>
        <Slider
          value={pinSize}
          onChange={(_, value) => setPinSize(value)}
          min={0.5}
          max={2}
          step={0.1}
          sx={{ color: '#d98d2a' }}
        />
      </Paper>



      {/* Leaflet Map */}
      <Box sx={{ 
        position: 'absolute',
        top: 120,
        left: 16,
        right: 16,
        bottom: 16,
        border: '2px solid #d98d2a',
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {map?.imageUrl && (
          <MapContainer
            ref={mapRef}
            center={[50, 50]}
            zoom={2}
            style={{ height: '100%', width: '100%', backgroundColor: '#1a1a1a' }}
            crs={L.CRS.Simple}
            minZoom={1}
            maxZoom={10}
          >
            <ImageOverlay
              url={map.imageUrl}
              bounds={imageBounds}
            />
            
            <MapClickHandler 
              isAddingPin={isAddingPin} 
              onAddPin={handleAddPin}
              isUltraAuth={isUltraAuth}
            />
            
            {/* Render Pins */}
            {pins
              .filter(pin => visibleCategories.has(pin.category))
              .map((pin) => (
              <Marker
                key={pin.id}
                position={[pin.lat || 50, pin.lng || 50]}
                icon={createCustomIcon(pin.category, highlightedPinId === pin.id ? pinSize * 1.3 : pinSize)}
                eventHandlers={{
                  click: () => handlePinClick(pin)
                }}
              >
                {/* Hover tooltip for quick preview */}
                <LeafletTooltip 
                  direction="top" 
                  offset={[0, -10]}
                  opacity={0.95}
                  className="pin-tooltip"
                >
                  <div style={{ 
                    minWidth: '200px', 
                    maxWidth: '320px',
                    lineHeight: '1.4'
                  }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      marginBottom: '6px', 
                      color: '#d98d2a',
                      fontSize: '0.9rem'
                    }}>
                      {pin.title}
                    </div>
                    {pin.description && (
                      <div style={{ 
                        marginBottom: '8px', 
                        fontSize: '0.8rem', 
                        color: '#ccc',
                        lineHeight: '1.3',
                        wordWrap: 'break-word',
                        maxHeight: '4.5em',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {(() => {
                          // Smart truncation: try to break at sentences first, then words
                          if (pin.description.length <= 120) {
                            return pin.description;
                          }
                          
                          // Try to find a sentence break within reasonable length
                          const sentenceBreak = pin.description.substring(0, 120).lastIndexOf('.');
                          if (sentenceBreak > 60) {
                            return pin.description.substring(0, sentenceBreak + 1);
                          }
                          
                          // Fall back to word break
                          const wordBreak = pin.description.substring(0, 115).lastIndexOf(' ');
                          if (wordBreak > 60) {
                            return pin.description.substring(0, wordBreak) + '...';
                          }
                          
                          // Final fallback to character limit
                          return pin.description.substring(0, 112) + '...';
                        })()}
                      </div>
                    )}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginTop: 'auto'
                    }}>
                      <span style={{ 
                        backgroundColor: PIN_CATEGORIES.find(cat => cat.value === pin.category)?.color || '#666',
                        color: 'white',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}>
                        {PIN_CATEGORIES.find(cat => cat.value === pin.category)?.label || 'Other'}
                      </span>
                      <span style={{ 
                        color: '#999', 
                        fontStyle: 'italic', 
                        fontSize: '0.7rem',
                        marginLeft: '8px'
                      }}>
                        Click for {pin.description && pin.description.length > 120 ? 'full details' : 'details'}
                      </span>
                    </div>
                  </div>
                </LeafletTooltip>
              </Marker>
            ))}
          </MapContainer>
        )}
      </Box>

      {/* Controls */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 24, 
        right: 24, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1 
      }}>
        {isUltraAuth && (
          <Tooltip title={isAddingPin ? "Cancel Adding Pin" : "Add Pin"}>
            <Fab
              onClick={() => setIsAddingPin(!isAddingPin)}
              sx={{
                backgroundColor: isAddingPin ? '#ff6b6b' : '#d98d2a',
                color: 'white',
                '&:hover': {
                  backgroundColor: isAddingPin ? '#ff5252' : '#c87d26'
                }
              }}
            >
              {isAddingPin ? <CloseIcon /> : <AddIcon />}
            </Fab>
          </Tooltip>
        )}
      </Box>

      {/* Pin Dialog */}
      <PinDialog
        open={pinDialog.open}
        pin={pinDialog.pin}
        onClose={() => setPinDialog({ open: false, lat: 0, lng: 0, pin: null })}
        onSave={handlePinSave}
        onDelete={isUltraAuth ? handlePinDelete : null}
        isEditable={isUltraAuth}
      />
    </Box>
  );
};

// Pin Dialog Component (same as before but cleaner)
const PinDialog = ({ open, pin, onClose, onSave, onDelete, isEditable }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'location'
  });

  useEffect(() => {
    if (pin) {
      setFormData({
        title: pin.title || '',
        description: pin.description || '',
        category: pin.category || 'location'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'location'
      });
    }
  }, [pin]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    onSave(formData);
  };

  const selectedCategory = PIN_CATEGORIES.find(cat => cat.value === formData.category) || PIN_CATEGORIES[0];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          backgroundColor: '#3c3c3c',
          color: '#F5F5F5',
          border: '1px solid #555'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        color: '#d98d2a',
        fontFamily: '"Georgia", serif'
      }}>
        <Typography variant="h6">
          {pin ? 'Pin Details' : 'Add New Pin'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#F5F5F5' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Pin Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
            disabled={!isEditable}
            placeholder="e.g., 'Goblin Cave', 'Merchant Shop'"
            InputLabelProps={{ style: { color: '#d98d2a' } }}
            inputProps={{ style: { color: '#F5F5F5' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#d98d2a' },
                '&:hover fieldset': { borderColor: '#c87d26' },
                '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
              }
            }}
          />
          
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            disabled={!isEditable}
            placeholder="Additional details about this location..."
            InputLabelProps={{ style: { color: '#d98d2a' } }}
            inputProps={{ style: { color: '#F5F5F5' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#d98d2a' },
                '&:hover fieldset': { borderColor: '#c87d26' },
                '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
              }
            }}
          />
          
          <TextField
            fullWidth
            select
            label="Category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            margin="normal"
            disabled={!isEditable}
            InputLabelProps={{ style: { color: '#d98d2a' } }}
            inputProps={{ style: { color: '#F5F5F5' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#d98d2a' },
                '&:hover fieldset': { borderColor: '#c87d26' },
                '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
              }
            }}
          >
            {PIN_CATEGORIES.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: category.color
                    }}
                  />
                  {category.label}
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: '#2c2c2c', 
            borderRadius: 1,
            border: `1px solid ${selectedCategory.color}`
          }}>
            <Typography variant="body2" sx={{ color: selectedCategory.color, fontWeight: 'bold' }}>
              {selectedCategory.label}
            </Typography>
            <Typography variant="caption" sx={{ color: '#A9A9A9' }}>
              {selectedCategory.description}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        {pin && onDelete && isEditable && (
          <Button
            onClick={onDelete}
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
          >
            Delete Pin
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>
          {isEditable ? 'Cancel' : 'Close'}
        </Button>
        {isEditable && (
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.title.trim()}
            sx={{ 
              backgroundColor: '#d98d2a', 
              '&:hover': { backgroundColor: '#c87d26' } 
            }}
          >
            {pin ? 'Update' : 'Add'} Pin
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeafletMapViewer;